'use client';

// Server-seeded auth context for the sidebar nav + impersonation banners.
// The root layout (server) resolves the effective actor and impersonation
// cookie once per request and renders this provider, so SidebarNav and
// useImpersonationState render the correct tier stack + banner state on
// FIRST PAINT instead of flickering through a 'rep' fallback while a
// post-hydration fetch('/api/me') resolves.
//
// The client hook still fetches /api/me on mount to pick up role_version
// bumps (migration 025 access-token-hook v2) and cookie changes that
// happen mid-session — the seed here only fixes the first-paint race.

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import type { ImpersonationState } from '@/lib/auth/impersonation-state';
import type { User } from '@/types';
import type { EmployerNavSummary } from '@/lib/employer/nav-summary';

export type { EmployerNavSummary } from '@/lib/employer/nav-summary';

// `roles` (WS0 M0.3, #2050) is carried for capability-aware shell behavior
// and the M0.5/WS2 account-menu switcher. AppShell capability gates consume
// the full set; the tier stack still keys on the primary role (decision D13).
export type NavSessionActor = Pick<User, 'id' | 'role' | 'roles' | 'tenant_id' | 'email' | 'name'> | null;
export type NavSessionTenant = { id: string; name: string } | null | undefined;

// The tenant brand the root layout already resolved for THIS session's own
// tenant (#1774). Seeded here so the sidebar wordmark paints the tenant brand
// on first render with no flash, mirroring how actor/impersonation are seeded.
// Own-tenant only by construction: resolveBrand resolves the authed user's own
// tenant via RLS, so staff/super_admin (tenant_id null) carry a null brand and
// correctly see the platform default even while impersonating. null (no brand
// row / unbranded tenant) makes TenantBrand fall back to the platform default,
// so an unbranded tenant renders exactly as before.
export interface NavSessionBrand {
  brandName: string | null;
  logoLightUrl: string | null;
  logoDarkUrl: string | null;
  // The APPLIED root theme (light/dark) so the sidebar shows the matching logo
  // variant. Mirrors the theme the server painted onto <html>.
  appliedTheme: 'light' | 'dark';
}

export interface NavSession {
  actor: NavSessionActor;
  employer: EmployerNavSummary | null | undefined;
  beginEmployerSummaryRefresh: () => number;
  updateEmployerSummary: (summary: EmployerNavSummary | null, requestId: number) => void;
  employerSummaryRevision: number;
  invalidateEmployerSummary: () => void;
  actorRevision: number;
  tenant: NavSessionTenant;
  impersonation: ImpersonationState;
  brand?: NavSessionBrand | null;
  beginActorRefresh: () => number;
  updateLiveSession: (
    actor: NavSessionActor,
    tenant: NavSessionTenant,
    expectedRevision: number,
    requestId: number
  ) => void;
  updateImpersonation: (impersonation: ImpersonationState) => void;
}

const NavSessionContext = createContext<NavSession | null>(null);

export function NavSessionProvider({
  actor,
  impersonation,
  brand = null,
  children,
}: {
  actor: NavSessionActor;
  impersonation: ImpersonationState;
  brand?: NavSessionBrand | null;
  children: ReactNode;
}) {
  const [actorState, setActorState] = useState(() => ({
    seed: actor,
    current: actor,
    tenant: undefined as NavSessionTenant,
    revision: 0,
    acceptedRequestId: 0,
  }));
  const [impersonationState, setImpersonationState] = useState(() => ({
    seed: impersonation,
    current: impersonation,
  }));
  // Only the employer shell loads this; undefined means not loaded.
  const [employerState, setEmployerState] = useState(() => ({
    current: undefined as EmployerNavSummary | null | undefined,
    acceptedRequestId: 0,
  }));
  const latestEmployerRequest = useRef(0);
  const beginEmployerSummaryRefresh = useCallback(() => {
    latestEmployerRequest.current += 1;
    return latestEmployerRequest.current;
  }, []);
  const updateEmployerSummary = useCallback((current: EmployerNavSummary | null, requestId: number) => {
    // Desktop and mobile rails can both be mounted. Order successful writes
    // globally; a failed or cancelled newer request must not block a good one.
    setEmployerState((state) => requestId > state.acceptedRequestId
      ? { current, acceptedRequestId: requestId }
      : state);
  }, []);
  const [employerSummaryRevision, setEmployerSummaryRevision] = useState(0);
  const invalidateEmployerSummary = useCallback(() => {
    setEmployerSummaryRevision((revision) => revision + 1);
  }, []);
  const latestActorRequest = useRef(0);
  // A router refresh can deliver a newer server seed without remounting this
  // client provider. Adjust during render so that authoritative server props
  // replace any older /api/me result without a stale intermediate effect.
  let resolvedActorState = actorState;
  if (resolvedActorState.seed !== actor) {
    resolvedActorState = {
      seed: actor,
      current: actor,
      tenant: undefined,
      revision: resolvedActorState.revision + 1,
      acceptedRequestId: resolvedActorState.acceptedRequestId,
    };
    setActorState(resolvedActorState);
  }
  let resolvedImpersonationState = impersonationState;
  if (resolvedImpersonationState.seed !== impersonation) {
    resolvedImpersonationState = {
      seed: impersonation,
      current: impersonation,
    };
    setImpersonationState(resolvedImpersonationState);
  }
  const beginActorRefresh = useCallback(() => {
    latestActorRequest.current += 1;
    return latestActorRequest.current;
  }, []);
  const updateLiveSession = useCallback((
    current: NavSessionActor,
    tenant: NavSessionTenant,
    expectedRevision: number,
    requestId: number
  ) => {
    setActorState((state) =>
      state.revision === expectedRevision
        && requestId > state.acceptedRequestId
        ? { ...state, current, tenant, acceptedRequestId: requestId }
        : state
    );
  }, []);
  const updateImpersonation = useCallback((current: ImpersonationState) => {
    setImpersonationState((state) => ({ ...state, current }));
  }, []);

  return (
    <NavSessionContext.Provider
      value={{
        actor: resolvedActorState.current,
        actorRevision: resolvedActorState.revision,
        employer: employerState.current,
        beginEmployerSummaryRefresh,
        updateEmployerSummary,
        employerSummaryRevision,
        invalidateEmployerSummary,
        tenant: resolvedActorState.tenant,
        impersonation: resolvedImpersonationState.current,
        brand,
        beginActorRefresh,
        updateLiveSession,
        updateImpersonation,
      }}
    >
      {children}
    </NavSessionContext.Provider>
  );
}

// Returns the server-seeded session when a provider is mounted, or null
// when it isn't (e.g., root-less render environments in unit tests that
// don't wrap with the provider). Consumers fall back to their old
// fetch-on-mount path when this returns null, keeping the component
// usable without provider wiring.
export function useOptionalNavSession(): NavSession | null {
  return useContext(NavSessionContext);
}
